import {Plan, School, SchoolClass, Subscription, User} from "models"
import React, {useMemo} from "react"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component"
import {dig} from "diggerize"
import Heading from "components/utils/heading"
import memo from "set-state-compare/build/memo"
import PlanCard from "./plan-card"
import PropTypes from "prop-types"
import propTypesExact from "prop-types-exact"
import SchoolClassesSelect from "components/school-classes/select"
import {Text} from "shared/base"
import useCurrentUser from "@kaspernj/api-maker/build/use-current-user"
import useI18n from "i18n-on-steroids/build/src/use-i18n.js"
import {View} from "react-native"

/**
 * @typedef {object} ComponentsPlansSignUpModalStep1Props
 * @property {unknown} onPlanChoosen
 * @property {unknown} onSchoolClassChanged
 * @property {unknown} resource
 */

/**
 * @typedef {object} ComponentsPlansSignUpModalStep1State
 * @property {unknown} currentSubscriptions
 * @property {unknown} plans
 * @property {unknown} schoolClassesQuery
 */

/**
 * @extends {ShapeComponent<ComponentsPlansSignUpModalStep1Props, ComponentsPlansSignUpModalStep1State>}
 */
class ComponentsPlansSignUpModalStep1 extends ShapeComponent {
  /** @type {ComponentsPlansSignUpModalStep1State} */
  state = {
    currentSubscriptions: undefined,
    plans: undefined,
    schoolClassesQuery: undefined
  }

  static propTypes = propTypesExact({
    onPlanChoosen: PropTypes.func.isRequired,
    onSchoolClassChanged: PropTypes.func.isRequired,
    resource: PropTypes.oneOfType([PropTypes.instanceOf(School), PropTypes.instanceOf(SchoolClass), PropTypes.instanceOf(User)])
  })

  setup() {
    const {t} = useI18n({namespace: "js.components.plans.sign_up_modal.step1"})

    this.t = t
    this.currentUser = useCurrentUser()


    useMemo(() => {
      this.tt.loadPlans()
    }, [])

    useMemo(() => {
      if (this.currentUser) {
        this.setState({
          schoolClassesQuery: SchoolClass
            .ransack({
              s: "current_translation_name",
              with_contact_id: this.currentUser.contactId()
            })
            .preload(["school.account", "school.country"])
            .select({
              Account: ["id"],
              Country: ["countryCode"],
              School: ["id", "name"],
              SchoolClass: ["contactId", "id", "name"]
            })
            .groupBy("id")
        })
      } else {
        this.setState({
          schoolClassesQuery: undefined
        })
      }
    }, [this.currentUser?.id()])

    useMemo(() => {
      if (this.currentUser && this.p.resource) {
        this.tt.loadCurrentSubsriptions()
      } else {
        this.setState({currentSubscriptions: undefined})
      }
    }, [this.currentUser?.id(), this.p.resource?.id()])
  }

  render() {
    return (
      <View dataSet={this.rootViewDataSet ||= {component: "plans/sign-up-modal/step1"}}>
        <View style={this.headerWrapStyle ||= {display: "flex", alignItems: "center"}}>
          <Heading>
            {this.t(".choose_the_plan_that_fits_you")}
          </Heading>
          <View style={this.schoolClassRowStyle ||= {display: "flex", flexDirection: "row", alignItems: "center"}}>
            <Text style={this.schoolClassLabelStyle ||= {marginRight: 9}}>
              {this.t(".you_are_about_to_choose_a_plan_for_the_following_class")}
            </Text>
            {this.s.schoolClassesQuery &&
              <View style={this.schoolClassSelectWrapStyle ||= {width: 140}}>
                <SchoolClassesSelect
                  defaultValue={this.p.resource instanceof SchoolClass && this.p.resource?.id()}
                  id="school_class"
                  onChange={this.tt.onSchoolClassChanged}
                  query={this.s.schoolClassesQuery}
                />
              </View>
            }
          </View>
          <View style={this.planCardsRowStyle ||= {display: "flex", flexDirection: "row", marginTop: 20}}>
            {this.s.plans?.map((plan) =>
              <View key={plan.id()} style={this.planViewStyle ||= {width: 360, marginLeft: 10, marginRight: 10}}>
                <PlanCard
                  current={this.tt.isPlanCurrent(plan)}
                  onPlanChoosen={this.p.onPlanChoosen}
                  plan={plan}
                />
              </View>
            )}
          </View>
        </View>
      </View>
    )
  }

  isPlanCurrent = (plan) => {
    if (this.s.currentSubscriptions?.length === 0 && plan.identifier() == "free") {
      return true
    }

    return Boolean(this.s.currentSubscriptions?.find((subscription) => subscription.planId() == plan.id()))
  }

  loadCurrentSubsriptions = async () => {
    const currentSubscriptions = await Subscription
      .ransack({
        contact_id_eq: this.p.resource.contactId(),
        with_active_period: 1
      })
      .select({Subscription: ["planId"]})
      .toArray()

    this.setState({currentSubscriptions})
  }

  loadPlans = async () => {
    const plans = await Plan
      .ransack({
        show_in_sign_up_modal_true: 1,
        s: "position"
      })
      .preload(["resources.periods.prices"])
      .select({
        Plan: ["description", "descriptionWithInlineStyling", "id", "identifier", "name", "subTitle"],
        PlanResource: ["id", "resourceName"],
        PlanResourcePeriod: ["id", "months", "name"],
        PlanResourcePeriodPrice: ["id", "price", "priceCents", "priceCurrency"]
      })
      .toArray()

    if (plans.length <= 0) throw new Error("Couldn't find any plans to show")

    this.setState({plans})
  }

  onSchoolClassChanged = ({options}) => {
    const schoolClass = dig(options, 0, "schoolClass")

    this.p.onSchoolClassChanged(schoolClass)
  }
}

export default memo(shapeComponent(ComponentsPlansSignUpModalStep1))
